import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { apiFetch } from './apiBase';

const SOURCE_TO_SCOPE = {
  'payroll-recon': 'payroll',
  'benefits-recon': 'benefits',
  'time-recon': 'time',
};

function SnapshotRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-700/80 pb-2 last:border-0">
      <dt className="text-slate-400">{label}</dt>
      <dd className="font-mono font-medium text-slate-200 tabular-nums">{value}</dd>
    </div>
  );
}

/**
 * Scope counts for WT source list mapping packs (payroll / benefits / time).
 */
export default function MappingSnapshot({ source, label }) {
  const [scopeStats, setScopeStats] = useState(null);
  const [exclusions, setExclusions] = useState(null);
  const [loading, setLoading] = useState(true);

  const scopeId = SOURCE_TO_SCOPE[source];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      apiFetch('/api/recon/scopes').catch(() => null),
      apiFetch('/api/recon/exclusions').catch(() => null),
    ]).then(([scopes, excl]) => {
      if (cancelled) return;
      setScopeStats(scopes?.scopes || null);
      setExclusions(excl);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [source]);

  if (!scopeId) return null;

  const stats = scopeStats?.[scopeId];
  const scopeLabel = stats?.label || label;
  const usesHours = scopeId === 'time';

  return (
    <div className="card-panel p-5">
      <h3 className="text-sm font-semibold text-slate-100">Mapping snapshot</h3>
      <p className="mt-1 text-xs text-cyan-500/90">{scopeLabel}</p>

      {loading ? (
        <p className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading counts…
        </p>
      ) : (
        <dl className="mt-3 space-y-2 text-sm">
          <SnapshotRow
            label="Oracle elements in scope"
            value={stats?.mappedElements?.toLocaleString() ?? '—'}
          />
          <SnapshotRow
            label="ADP wage types in scope"
            value={stats?.mappedWageTypes?.toLocaleString() ?? '—'}
          />
          {usesHours && (
            <SnapshotRow label="Time elements" value={stats?.mappedElements?.toLocaleString() ?? '—'} />
          )}
          <SnapshotRow
            label="Excluded elements"
            value={(exclusions?.oracleElements?.length ?? 0).toLocaleString()}
          />
          <SnapshotRow
            label="Excluded wage types"
            value={(exclusions?.adpWageTypeCodes?.length ?? 0).toLocaleString()}
          />
          {stats?.mappingRowsIncluded != null && (
            <SnapshotRow
              label="Mapping rows (included)"
              value={stats.mappingRowsIncluded.toLocaleString()}
            />
          )}
        </dl>
      )}

      <p className="mt-3 text-xs text-slate-500">
        Edit rows below or manage exclusions on the <strong>Recon exclusions</strong> tab.
      </p>
    </div>
  );
}
